import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Clock, ArrowRight, BookOpen } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogSection = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);

  const fetchPosts = useCallback(() => {
    axios.get(`${API}/blog`)
      .then(r => setPosts(r.data.slice(0, 3)))
      .catch(() => { /* Silently handled - blog section is optional */ });
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const labels = {
    en: { title: "CARE TIPS", subtitle: "Latest from Our Blog", readMore: "Read More", viewAll: "View All Articles" },
    es: { title: "CONSEJOS", subtitle: "Ultimas Publicaciones", readMore: "Leer Mas", viewAll: "Ver Todos" },
    pt: { title: "DICAS", subtitle: "Ultimas Publicacoes", readMore: "Leia Mais", viewAll: "Ver Todos" },
    fr: { title: "CONSEILS", subtitle: "Derniers Articles", readMore: "Lire Plus", viewAll: "Voir Tout" },
  }[language] || { title: "CARE TIPS", subtitle: "Latest from Our Blog", readMore: "Read More", viewAll: "View All Articles" };

  if (posts.length === 0) return null;

  return (
    <section data-testid="blog-section" className="py-24 bg-[#F3F0E7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{labels.title}</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mt-3">{labels.subtitle}</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {posts.map((post) => (
            <Card key={post.id} data-testid={`blog-card-${post.id}`} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 overflow-hidden bg-white group">
              <div className="h-48 overflow-hidden">
                <img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold text-[#003A4B] bg-[#003A4B]/10 px-3 py-1 rounded-full">{post.category}</span>
                  <span className="flex items-center gap-1 text-xs text-[#718096]"><Clock className="w-3 h-3" />{post.read_time}</span>
                </div>
                <h3 className="font-semibold text-[#1A2332] mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-[#4A5568] mb-4 line-clamp-2">{post.excerpt}</p>
                <Link to={`/blog/${post.id}`} className="flex items-center gap-1 text-[#C89D4B] text-sm font-medium hover:gap-2 transition-all">
                  {labels.readMore} <ArrowRight className="w-4 h-4" />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/blog">
            <Button variant="outline" className="rounded-full px-8 py-5 border-2 border-[#003A4B] text-[#003A4B] hover:bg-[#003A4B]/5">
              <BookOpen className="w-5 h-5 mr-2" /> {labels.viewAll}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
